import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import TextInput from "../ui/TextInput";
import { Tooltip } from "@mui/material";
import {
    getCompanyFileCategories,
    createCompanyFileCategory,
    updateCompanyFileCategory,
    deleteCompanyFileCategory,
} from "../../api/companyFileCategories";
import { CompanyFileCategory } from "../../types/requestsOutputs";
import {
    getCompanyFileTags,
    createCompanyFileTag,
    updateCompanyFileTag,
    deleteCompanyFileTag,
    CompanyFileTag,
    getCompanyUsageTypeMetadatas,
    createCompanyUsageTypeMetadata,
    updateCompanyUsageTypeMetadata,
    deleteCompanyUsageTypeMetadata,
    CompanyUsageTypeMetadata,
    getCompanyRightsTermMetadatas,
    createCompanyRightsTermMetadata,
    updateCompanyRightsTermMetadata,
    deleteCompanyRightsTermMetadata,
    CompanyRightsTermMetadata,
    getCompanyTerritoryMetadatas,
    createCompanyTerritoryMetadata,
    updateCompanyTerritoryMetadata,
    deleteCompanyTerritoryMetadata,
    CompanyTerritoryMetadata,
    getCompanyAllowedMediaMetadatas,
    createCompanyAllowedMediaMetadata,
    updateCompanyAllowedMediaMetadata,
    deleteCompanyAllowedMediaMetadata,
    CompanyAllowedMediaMetadata,
} from "../../api/companyMetadata";
import styles from "./MetadataManagementModal.module.css";

interface CustomMetadataManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type MetadataItem =
    | CompanyFileCategory
    | CompanyFileTag
    | CompanyUsageTypeMetadata
    | CompanyRightsTermMetadata
    | CompanyTerritoryMetadata
    | CompanyAllowedMediaMetadata;

type TabType = "categories" | "tags" | "usageType" | "rightsTerm" | "territory" | "allowedMedia";

const CustomMetadataManagementModal: React.FC<CustomMetadataManagementModalProps> = ({ isOpen, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [activeTab, setActiveTab] = useState<TabType>("categories");
    const [categories, setCategories] = useState<CompanyFileCategory[]>([]);
    const [tags, setTags] = useState<CompanyFileTag[]>([]);
    const [usageTypes, setUsageTypes] = useState<CompanyUsageTypeMetadata[]>([]);
    const [rightsTerms, setRightsTerms] = useState<CompanyRightsTermMetadata[]>([]);
    const [territories, setTerritories] = useState<CompanyTerritoryMetadata[]>([]);
    const [allowedMedia, setAllowedMedia] = useState<CompanyAllowedMediaMetadata[]>([]);

    const [loading, setLoading] = useState(false);
    const [newItemName, setNewItemName] = useState("");
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [error, setError] = useState<string | null>(null);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            fetchAllMetadata();
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
        // fetch-on-mount when modal opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, handleOutsideClick]);

    const fetchAllMetadata = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            const [categoriesData, tagsData, usageTypesData, rightsTermsData, territoriesData, allowedMediaData] =
                await Promise.all([
                    getCompanyFileCategories({ token }),
                    getCompanyFileTags({ token }),
                    getCompanyUsageTypeMetadatas({ token }),
                    getCompanyRightsTermMetadatas({ token }),
                    getCompanyTerritoryMetadatas({ token }),
                    getCompanyAllowedMediaMetadatas({ token }),
                ]);

            setCategories(categoriesData);
            setTags(tagsData);
            setUsageTypes(usageTypesData);
            setRightsTerms(rightsTermsData);
            setTerritories(territoriesData);
            setAllowedMedia(allowedMediaData);
        } catch (error) {
            console.error("Error fetching metadata:", error);
            setError(t("errorFetchingMetadata") || "Error fetching metadata");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateItem = async () => {
        if (!newItemName.trim()) return;

        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            switch (activeTab) {
                case "categories":
                    await createCompanyFileCategory({ token, name: newItemName.trim() });
                    break;
                case "tags":
                    await createCompanyFileTag({ token, name: newItemName.trim() });
                    break;
                case "usageType":
                    await createCompanyUsageTypeMetadata({ token, name: newItemName.trim() });
                    break;
                case "rightsTerm":
                    await createCompanyRightsTermMetadata({ token, name: newItemName.trim() });
                    break;
                case "territory":
                    await createCompanyTerritoryMetadata({ token, name: newItemName.trim() });
                    break;
                case "allowedMedia":
                    await createCompanyAllowedMediaMetadata({ token, name: newItemName.trim() });
                    break;
            }

            setNewItemName("");
            await fetchAllMetadata();
        } catch (error) {
            console.error("Error creating item:", error);
            setError(t("errorCreatingItem") || "Error creating item");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateItem = async (itemId: string) => {
        if (!editName.trim()) return;

        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            switch (activeTab) {
                case "categories":
                    await updateCompanyFileCategory({ token, categoryId: itemId, newName: editName.trim() });
                    break;
                case "tags":
                    await updateCompanyFileTag({ token, tagId: itemId, newName: editName.trim() });
                    break;
                case "usageType":
                    await updateCompanyUsageTypeMetadata({
                        token,
                        usageTypeMetadataId: itemId,
                        newName: editName.trim(),
                    });
                    break;
                case "rightsTerm":
                    await updateCompanyRightsTermMetadata({
                        token,
                        rightsTermMetadataId: itemId,
                        newName: editName.trim(),
                    });
                    break;
                case "territory":
                    await updateCompanyTerritoryMetadata({
                        token,
                        territoryMetadataId: itemId,
                        newName: editName.trim(),
                    });
                    break;
                case "allowedMedia":
                    await updateCompanyAllowedMediaMetadata({
                        token,
                        allowedMediaMetadataId: itemId,
                        newName: editName.trim(),
                    });
                    break;
            }

            setEditingItemId(null);
            setEditName("");
            await fetchAllMetadata();
        } catch (error) {
            console.error("Error updating item:", error);
            setError(t("errorUpdatingItem") || "Error updating item");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = async (itemId: string, itemName: string) => {
        if (!window.confirm(t("confirmDeleteItem") || `Are you sure you want to delete "${itemName}"?`)) return;

        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            let result: { fileCount?: number } = {};

            switch (activeTab) {
                case "categories":
                    result = await deleteCompanyFileCategory({ token, categoryId: itemId });
                    break;
                case "tags":
                    result = await deleteCompanyFileTag({ token, tagId: itemId });
                    break;
                case "usageType":
                    result = await deleteCompanyUsageTypeMetadata({ token, usageTypeMetadataId: itemId });
                    break;
                case "rightsTerm":
                    result = await deleteCompanyRightsTermMetadata({ token, rightsTermMetadataId: itemId });
                    break;
                case "territory":
                    result = await deleteCompanyTerritoryMetadata({ token, territoryMetadataId: itemId });
                    break;
                case "allowedMedia":
                    result = await deleteCompanyAllowedMediaMetadata({ token, allowedMediaMetadataId: itemId });
                    break;
            }

            if (result.fileCount && result.fileCount > 0) {
                alert(t("cannotDeleteItemWithFiles") || `Cannot delete item. It has ${result.fileCount} linked files.`);
                return;
            }

            await fetchAllMetadata();
        } catch (error) {
            console.error("Error deleting item:", error);
            setError(t("errorDeletingItem") || "Error deleting item");
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (item: MetadataItem) => {
        setEditingItemId(item.id);
        setEditName(item.name);
    };

    const cancelEditing = () => {
        setEditingItemId(null);
        setEditName("");
    };

    const getCurrentItems = (): MetadataItem[] => {
        switch (activeTab) {
            case "categories":
                return categories;
            case "tags":
                return tags;
            case "usageType":
                return usageTypes;
            case "rightsTerm":
                return rightsTerms;
            case "territory":
                return territories;
            case "allowedMedia":
                return allowedMedia;
            default:
                return [];
        }
    };

    const getTabLabel = (tab: TabType): string => {
        switch (tab) {
            case "categories":
                return `${t("tag")} 1` || "Tag 1";
            case "tags":
                return `${t("tag")} 2` || "Tag 2";
            case "usageType":
                return t("metadataUsageType") || "Usage Type";
            case "rightsTerm":
                return t("metadataRightsTerm") || "Rights Term";
            case "territory":
                return t("metadataTerritory") || "Territory";
            case "allowedMedia":
                return t("metadataAllowedMedia") || "Allowed Media";
            default:
                return "";
        }
    };

    const getCurrentTabLabel = (): string => {
        return getTabLabel(activeTab);
    };

    if (!isOpen) return null;

    const currentItems = getCurrentItems();
    const tabs: TabType[] = ["categories", "tags", "usageType", "rightsTerm", "territory", "allowedMedia"];

    const modal = (
        <div className={styles.overlay}>
            <div className={`${styles.modal} ${styles.metaManageModal}`} ref={modalContentRef}>
                <div className={styles.header}>
                    <h2 className={styles.title}>{t("manageCustomMetadata") || "Manage Custom Metadata"}</h2>
                    <button type="button" className={styles.close} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.tabs}>
                    {tabs.map((tab) => (
                        <button
                            type="button"
                            key={tab}
                            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
                            onClick={() => {
                                setActiveTab(tab);
                                setNewItemName("");
                                setEditingItemId(null);
                                setEditName("");
                                setError(null);
                            }}
                        >
                            {getTabLabel(tab)}
                        </button>
                    ))}
                </div>

                <div className={styles.body}>
                    {error && <div className={styles.error}>{error}</div>}

                    <div className={styles.createSection}>
                        <h3 className={styles.sectionTitle}>
                            {t("createNew") || "Create New"} {getCurrentTabLabel()}
                        </h3>
                        <div className={styles.createForm}>
                            <TextInput
                                placeholder={t("itemName") || "Enter name"}
                                value={newItemName}
                                onChange={(e) => setNewItemName(e.target.value)}
                                size="small"
                            />
                            <Button
                                className={`${styles.createBtn} btn-meta-create`}
                                label={t("create") || "Create"}
                                onClick={handleCreateItem}
                                disabled={loading || !newItemName.trim()}
                            />
                        </div>
                    </div>

                    <div>
                        <h3 className={styles.sectionTitle}>
                            {t("existing") || "Existing"} {getCurrentTabLabel()}
                        </h3>
                        {loading && currentItems.length === 0 ? (
                            <div className={styles.loading}>{t("loading") || "Loading..."}</div>
                        ) : currentItems.length === 0 ? (
                            <div className={styles.empty}>{t("noItemsFound") || "No items found"}</div>
                        ) : (
                            <div className={styles.list}>
                                {currentItems.map((item) => (
                                    <div key={item.id} className={styles.item}>
                                        {editingItemId === item.id ? (
                                            <div className={styles.editForm}>
                                                <TextInput
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    placeholder={t("newName") || "Enter new name"}
                                                    size="small"
                                                />
                                                <div className={styles.editActions}>
                                                    <Button
                                                        className={`${styles.ghostBtn} btn-meta-ghost`}
                                                        label={t("save") || "Save"}
                                                        onClick={() => handleUpdateItem(item.id)}
                                                        disabled={loading || !editName.trim()}
                                                        isSmall
                                                    />
                                                    <Button
                                                        className={`${styles.ghostBtn} btn-meta-ghost`}
                                                        label={t("cancel") || "Cancel"}
                                                        onClick={cancelEditing}
                                                        disabled={loading}
                                                        isSmall
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.row}>
                                                <div className={styles.info}>
                                                    <span className={styles.name}>{item.name}</span>
                                                    <span className={styles.count}>
                                                        ({item.fileCount} {t("files") || "files"})
                                                    </span>
                                                </div>
                                                <div className={styles.actions}>
                                                    <Button
                                                        className={`${styles.ghostBtn} btn-meta-ghost`}
                                                        label={t("edit") || "Edit"}
                                                        onClick={() => startEditing(item)}
                                                        disabled={loading}
                                                        isSmall
                                                    />
                                                    <Tooltip
                                                        title={
                                                            item.fileCount > 0 ? t("disabledDeleteItem") : t("delete")
                                                        }
                                                        slotProps={{
                                                            popper: { sx: { zIndex: 11000 } },
                                                        }}
                                                    >
                                                        <span>
                                                            <Button
                                                                className={`${styles.dangerBtn} btn-meta-danger`}
                                                                label={t("delete") || "Delete"}
                                                                onClick={() => handleDeleteItem(item.id, item.name)}
                                                                disabled={loading || item.fileCount > 0}
                                                                isSmall
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default CustomMetadataManagementModal;
